
import React from "react";
import { Schemes, Utils } from "../../../../utils";
import { i18nManager } from "../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { Elements } from "../../../shared/Elements";
import MediaInput from "../../../shared/MediaInput";
import StatefulLink from "../../../shared/StatefulLink";
import { ConfirmDialog, Siren } from "../../../shared/ConfirmDialog";
import LoadingGif from "../../../../assets/images/loading_circle.gif";
import { Button, ButtonManageRef, Checkbox, Column, DateHelper, DateTimeInput, Dropdown, Form, FormControl, NoseurObject, Scheme, StringHelper, Table, TableSelectionMode, TextAreaInput, TextInput, useScreenDimension } from "@rivtn/noseur";
import { ApiResponseData, BaseService, ConsusService, HostService, IHost, IHostEvent, IMember, IProfileWebsite, ITeam, ITournament, IUploadedFile, MemberService, SanitizedResponse, TournamentService } from "../../../../services";

export interface ManageTournamentsProps {
    host: IHost;
    profile: IMember;
}

function ManageTournaments(props: ManageTournamentsProps) {

    const { host } = props;
    const now = new Date().getTime();
    const { width } = useScreenDimension();
    const navigate = ReactRouterDom.useNavigate();
    const consusService = ConsusService.getInstance();
    const tournamentService = TournamentService.getInstance();
    const [profile, _] = React.useState<IMember>(props.profile);
    const labels = i18nManager.Labels.host.settings.manage_tournaments;
    const [tournaments, setTournaments] = React.useState<ApiResponseData<ITournament>>({});
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [currencySign, setCurrencySign] = React.useState<string>(i18nManager.getCurrencySign(searchParams.get("curr") ?? "NGN"));
    const maxSponsors = 5; // TODO FETCH FROM PLAN
    const tournamentColorMap: NoseurObject<string> = {
        draft: "#C58C49",
        ended: "#D55C5C",
        closed: "#B449C5",
        pending: "#90BA19",
        ongoing: "#49C564",
        upcoming: "#2F3D88",
        cancelled: "#000000",
        in_registration: "#49A7C5",
    };

    React.useEffect(fetchTournaments, []);

    return (<div className="profile-settings team-settings" style={{ paddingTop: 0 }}>
        <div className="pagination">
            {Elements.buildPaginator(tournaments ?? {},
                ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, size }, setSearchParams, fetchTournaments),
                parseInt(searchParams.get("page") ?? "1"), { margin: 15, marginBottom: 5 })}
            {(width > 800) && Elements.buildSorter(labels.sorts, (sort) => Utils.updateSearchParamses({ sort }, setSearchParams, fetchTournaments), labels.sorts[0].label)}
        </div>
        <div style={{ display: "flex", marginTop: 10, justifyContent: "space-between", flexDirection: (width > 800 ? "row" : "column") }}>
            <Form scheme={Schemes.OMNEHOST} style={{ display: "flex", flexDirection: (width > 800 ? "row" : "column") }}>
                <FormControl isFieldset label={labels.name} style={{ marginRight: 15, width: (width > 800 ? 200 : "100%") }}>
                    <TextInput defaultValue={searchParams.get("name") ?? ""} onInputComplete={(v) => {
                        Utils.updateSearchParams("name", v, setSearchParams, fetchTournaments);
                    }} />
                </FormControl>
                {(width > 800) && (<>
                    <Dropdown selectedOptionIndex={parseInt(searchParams.get("status") ?? "0")} formControlProps={{
                        isFieldset: true,
                        label: labels.status,
                        style: { width: 150 }
                    }} options={labels.statuses} onSelectOption={(o) => {

                    }} />
                    <FormControl isFieldset label={labels.active_date} style={{ marginLeft: 15, width: 300 }}>
                        <TextInput />
                    </FormControl>
                </>)}
            </Form>
            {(width > 800) && (<Dropdown noIcon scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.currency, }}
                selectedOptionIndex={(i18nManager.CountriesAndCurrencies?.findIndex((p) => p?.currency_code === (searchParams.get("curr") ?? "NGN")) ?? 0)}
                textInputProps={{ style: { paddingLeft: 15 } }}
                options={i18nManager.CountriesAndCurrencies} optionMap={{
                    value: "currency_code",
                    label: "{currency_name} ({currency_code})",
                }} onSelectOption={(option) => {
                    Utils.updateSearchParams("curr", option.currency_code, setSearchParams, () => {
                        setCurrencySign(i18nManager.getCurrencySign(searchParams.get("curr") ?? "NGN"));
                        fetchTournaments();
                    });
                }} />)}
        </div>
        <hr style={{ margin: "20px 0px" }} />
        {!tournaments.content ? Elements.LOADING_STATE : (<Table stripedRows scheme={Schemes.OMNEHOST} data={tournaments.content} totalRecords={tournaments.total_elements} dataRefreshKeys={["external_id", "created_at"]}
            style={{ flex: 1 }} rowsPerPage={200} loadingState={Elements.LOADING_STATE} emptyState={Elements.buildEmptyListView("fa fa-trophy", (<span>
                {labels.empty_label_parts.no_tours_found}{" "}
                <a className="link" href={`/hosts/${host.public_id}/create-tournament`}>{labels.empty_label_parts.create}</a>{" "}
                {labels.empty_label_parts.a_tour}
            </span>))} selectionMode={TableSelectionMode.SINGLE} rowSelection={true} valuedRowProps={() => {
                return {
                    className: "host-tour-row"
                };
            }} onRowSelection={(tournament) => {
                navigate(`/hosts/${host.public_id}/tournament/${tournament.external_id}`);
                return false;
            }}>
            <Column className="table-c" doNotRender={width < 800} header={labels.table.headers.last_date_updated} dataKey="updated_at" template={(v) => Utils.formatToFineDate(v, true)} />
            <Column className="table-c" header={labels.table.headers.name} template={(v: ITournament) => (v.name === v.current_edition?.name ? v.name : `${v.name} (${v.current_edition?.name})`)} />
            <Column className="table-c" doNotRender={width < 800} header={labels.table.headers.prev_earns} dataKey="previous_earning" template={(v: number) => (`${currencySign} ${StringHelper.toPresentableMoneyValue(v)}`)} />
            <Column className="table-c" doNotRender={width < 800} header={labels.table.headers.forecast_earns} dataKey="forecast_earning" template={(v: number) => (`${currencySign} ${StringHelper.toPresentableMoneyValue(v)}`)} />
            <Column className="table-c" doNotRender={width < 800} header={labels.table.headers.unique_partis} template={(v: ITournament) => (`${v.current_edition?.meta?.total_registered_participants} ${labels.participants}`)} />
            <Column className="table-c" header={labels.table.headers.status} dataKey="{current_edition.status}" template={(v: string) => {
                const status = v.toLowerCase();
                return (<b style={{ color: tournamentColorMap[status] }}>{(labels.statuses_labels as any)[status]}</b>);
            }} />
        </Table>)}
    </div>);

    function fetchTournaments() {
        setTournaments({});
        tournamentService.queryTournaments({
            name: searchParams.get("name"),
            status: searchParams.get("status"),
            "host.external_id": host.external_id,
            sort: searchParams.get("sort") ?? "",
            page: searchParams.get("page") ?? "1",
            size: searchParams.get("size") ?? "100",
            currency_code: searchParams.get("curr") ?? "NGN",
        }).then(({ apiResponse }: SanitizedResponse<ITournament>) => {
            setTournaments(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default ManageTournaments;
